import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  errorToString,
  LoanApplication,
  pause,
  PersonaParty,
  PersonaVerificationRecord,
} from '@oneethos/shared'
import api from '../../api-client'
import { useAppState } from '../../hooks'
import IdentityVerificationCard, {
  VerificationStage,
} from './IdentityVerificationCard'

const POLL_INTERVAL_SECS = 15
const POLL_MAX_ATTEMPTS = 40

export function isPersonaVerificationComplete(
  verification?: PersonaVerificationRecord
): boolean {
  if (!verification) {
    return false
  }
  if (verification.verifiedAt) {
    return true
  }
  return verification.status === 'completed'
}

function verificationStage(
  verification?: PersonaVerificationRecord
): VerificationStage {
  if (isPersonaVerificationComplete(verification)) {
    return 'completed'
  }
  if (verification?.inquiryId) {
    return 'inProgress'
  }
  return 'notStarted'
}

function partyVerificationField(
  party: PersonaParty
): 'borrower' | 'coBorrower' {
  return party === 'borrower' ? 'borrower' : 'coBorrower'
}

export type IdentityVerificationProps = {
  party: PersonaParty
  loanApplicationId: string
  verification?: PersonaVerificationRecord
  onUpdated: (lapp: LoanApplication) => void
  /**
   * Runs before the verification request. Verification reads the persisted loan
   * application, so callers whose applicant data is not saved yet use this to
   * persist it first. Returning false cancels the request.
   */
  onBeforeVerify?: () => Promise<boolean>
}

type BypassPersonaButtonProps = {
  disabled: boolean
  onBypass: () => void
}

const BypassPersonaButton = ({ disabled, onBypass }: BypassPersonaButtonProps) => (
  <div className="alert alert-secondary mt-3">
    <div className="buttons">
      <button
        type="button"
        className="btn btn-secondary"
        disabled={disabled}
        onClick={onBypass}
      >
        Bypass Persona
      </button>
    </div>
  </div>
)

const IdentityVerification = ({
  party,
  loanApplicationId,
  verification,
  onUpdated,
  onBeforeVerify,
}: IdentityVerificationProps) => {
  const { tenant } = useAppState()
  const [searchParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  const onUpdatedRef = useRef(onUpdated)
  onUpdatedRef.current = onUpdated

  const stage = verificationStage(verification)
  const token = searchParams.get('t')
  const authQuery = token ? `?t=${encodeURIComponent(token)}` : ''
  const personaDisabled = Boolean(tenant.config?.personaDisabled)

  useEffect(() => {
    if (personaDisabled || stage !== 'inProgress') {
      return
    }

    let active = true

    const pollUntilVerified = async () => {
      for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
        await pause(POLL_INTERVAL_SECS)
        if (!active) {
          return
        }

        const { lapp } = await api.get(`/c/loanapps/${loanApplicationId}${authQuery}`)
        if (!active) {
          return
        }

        onUpdatedRef.current(lapp)

        const record = lapp.personaVerifications?.[partyVerificationField(party)]
        if (isPersonaVerificationComplete(record)) {
          return
        }
      }
    }

    void pollUntilVerified().catch((error) => {
      if (active) {
        toast.error(errorToString(error), { autoClose: false })
      }
    })

    return () => {
      active = false
    }
  }, [stage, loanApplicationId, authQuery, party, personaDisabled])

  if (personaDisabled) {
    return null
  }

  const handleBeginOrResend = async () => {
    setBusy(true)

    try {
      if (onBeforeVerify && !await onBeforeVerify()) {
        return
      }

      const lapp = await api.post(
        `/c/loanapps/${loanApplicationId}/persona-verification${authQuery}`,
        { party }
      )
      onUpdated(lapp)
    } catch (error) {
      toast.error(errorToString(error), { autoClose: false })
    } finally {
      setBusy(false)
    }
  }

  const handleBypass = async () => {
    setBusy(true)

    try {
      const lapp = await api.post(
        `/c/loanapps/${loanApplicationId}/persona-verification${authQuery}`,
        { party, bypass: true }
      )
      onUpdated(lapp)
    } catch (error) {
      toast.error(errorToString(error), { autoClose: false })
    } finally {
      setBusy(false)
    }
  }

  const showBypass =
    process.env.REACT_APP_NODE_ENV === 'test' && stage !== 'completed'

  return (
    <>
      <IdentityVerificationCard
        stage={stage}
        disabled={busy}
        onVerify={handleBeginOrResend}
      />
      {showBypass ? (
        <BypassPersonaButton
          disabled={busy}
          onBypass={handleBypass}
        />
      ) : null}
    </>
  )
}

export default IdentityVerification
